import {Location} from '@mintdata/mint-kit/api/models/Location';
import {PropName} from '@mintdata/mint-kit/api/properties/PropertySpec';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {Empty} from '@mintdata/mint-kit/api/types/Value';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {APositiveNumber} from '@mintdata/mint-kit/api/values/APositiveNumber';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {AFrame} from '@mintdata/mint-kit/api/values/frames/AFrame';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {JsonUtil} from '@mintdata/mint-kit/impl/util/JsonUtil';

export type EventPropName = PropName;

export class ChangeEvent<T>
{
    constructor(public readonly newValue:T)
    {
    }
}

export class AChangeEvent<T>
{
    constructor(public readonly oldValue:T,
                public readonly newValue:T)
    {
    }
}

export class AOpenChangedEvent
{
    constructor(public readonly isOpen:ABoolean,
                public readonly currentPageValue:AString,
                public readonly currentPageCaption:AString)
    {
    }
}

export class ASelectionChangeEvent<T>
{
    constructor(public readonly oldValue:T,
                public readonly newValue:T,
                public readonly oldIndex:ANumber,
                public readonly newIndex:ANumber)
    {
    }
}

export class AMultiSelectionChangeEvent<T>
{
    constructor(public readonly oldItems:IFrame,
                public readonly items:IFrame,
                public readonly addedItem:T,
                public readonly removedItem:T,
                public readonly isNewItem:ABoolean)
    {
    }
}

export class AValueRangeChangeEvent<T>
{
    constructor(public oldFromValue:T,
                public newFromValue:T,
                public oldToValue:T,
                public newToValue:T)
    {
    }
}

export class AFocusEvent
{
    public readonly itemValue:AString | Empty;

    constructor(event:React.FocusEvent<HTMLElement>, itemValue:AString = Empty)
    {
        this.itemValue = itemValue;
    }
}

export class AUIEvent
{
    public readonly detail:ANumber;

    constructor(event:React.UIEvent<HTMLElement>)
    {
        this.detail = ANumber.fromNumber(event.detail);
    }
}

export class AMouseEvent
{
    public readonly clientX:ANumber;
    public readonly clientY:ANumber;
    public readonly pageX:ANumber;
    public readonly pageY:ANumber;
    public readonly screenX:ANumber;
    public readonly screenY:ANumber;
    public readonly ctrlKey:ABoolean;
    public readonly metaKey:ABoolean;
    public readonly altKey:ABoolean;
    public readonly shiftKey:ABoolean;
    public readonly button:AString;
    public readonly buttons:ANumber;
    public readonly itemValue:AString | Empty;

    constructor(event:React.MouseEvent<HTMLElement>, itemValue:AString = Empty)
    {
        this.clientX = ANumber.fromNumber(event.clientX);
        this.clientY = ANumber.fromNumber(event.clientY);
        this.pageX = ANumber.fromNumber(event.pageX);
        this.pageY = ANumber.fromNumber(event.pageY);
        this.screenX = ANumber.fromNumber(event.screenX);
        this.screenY = ANumber.fromNumber(event.screenY);
        this.ctrlKey = ABoolean.fromBoolean(event.ctrlKey);
        this.metaKey = ABoolean.fromBoolean(event.metaKey);
        this.altKey = ABoolean.fromBoolean(event.altKey);
        this.shiftKey = ABoolean.fromBoolean(event.shiftKey);
        this.button = AString.fromString(
            event.button === 0
                ? 'left'
                : event.button === 1
                ? 'middle'
                : event.button === 2
                    ? 'right'
                    : event.button.toString()
        );
        this.buttons = ANumber.fromNumber(event.buttons);

        this.itemValue = itemValue;
    }
}

export class AWheelEvent extends AMouseEvent
{
    public readonly deltaMode:ANumber;
    public readonly deltaX:ANumber;
    public readonly deltaY:ANumber;
    public readonly deltaZ:ANumber;

    constructor(event:React.WheelEvent<HTMLElement>)
    {
        super(event);

        this.deltaMode = ANumber.fromNumber(event.deltaMode);
        this.deltaX = ANumber.fromNumber(event.deltaX);
        this.deltaY = ANumber.fromNumber(event.deltaY);
        this.deltaZ = ANumber.fromNumber(event.deltaZ);
    }
}

export class AKeyboardEvent
{
    public readonly metaKey:ABoolean;
    public readonly ctrlKey:ABoolean;
    public readonly altKey:ABoolean;
    public readonly shiftKey:ABoolean;
    public readonly key:AString;
    public readonly which:ANumber;

    constructor(event:React.KeyboardEvent<HTMLElement>)
    {
        this.ctrlKey = ABoolean.fromBoolean(event.ctrlKey);
        this.metaKey = ABoolean.fromBoolean(event.metaKey);
        this.altKey = ABoolean.fromBoolean(event.altKey);
        this.shiftKey = ABoolean.fromBoolean(event.shiftKey);
        this.key = AString.fromString(event.key);
        this.which = ANumber.fromNumber(event.which);
    }
}

export class AMapPointClickedEvent
{
    public readonly seriesIndex:ANumber;
    public readonly itemIndex:ANumber;
    public readonly mapX:ANumber;
    public readonly mapY:ANumber;
    public readonly pageX:ANumber;
    public readonly pageY:ANumber;

    constructor(seriesIndex:number, itemIndex:number, mapX:number, mapY:number, pageX:number, pageY:number)
    {
        this.seriesIndex = ANumber.fromNumber(seriesIndex);
        this.itemIndex = ANumber.fromNumber(itemIndex);
        this.mapX = ANumber.fromNumber(mapX);
        this.mapY = ANumber.fromNumber(mapY);
        this.pageX = ANumber.fromNumber(pageX);
        this.pageY = ANumber.fromNumber(pageY);
    }
}

export class AMapPointMouseHoverEvent
{
    public readonly seriesIndex:ANumber;
    public readonly itemIndex:ANumber;
    public readonly mapX:ANumber;
    public readonly mapY:ANumber;
    public readonly pageX:ANumber;
    public readonly pageY:ANumber;
    public readonly hovered:ABoolean;

    constructor(seriesIndex:number, itemIndex:number, mapX:number, mapY:number, pageX:number, pageY:number, hovered:boolean)
    {
        this.seriesIndex = ANumber.fromNumber(seriesIndex);
        this.itemIndex = ANumber.fromNumber(itemIndex);
        this.mapX = ANumber.fromNumber(mapX);
        this.mapY = ANumber.fromNumber(mapY);
        this.pageX = ANumber.fromNumber(pageX);
        this.pageY = ANumber.fromNumber(pageY);
        this.hovered = ABoolean.fromBoolean(hovered);
    }
}

export class AMapVisibleRegion
{
    public readonly from:IFrame;
    public readonly to:IFrame;

    constructor(from:Location, to:Location)
    {
        this.from = new AFrame(
            [
                new FrameField('lat', Type.Number), new FrameField('lon', Type.Number)
            ],
            [
                [
                    from.lat, from.long
                ]
            ]
        );
        this.to = new AFrame(
            [
                new FrameField('lat', Type.Number), new FrameField('lon', Type.Number)
            ],
            [
                [
                    to.lat, to.long
                ]
            ]
        );
    }
}

export class AMapOnPanChangeEvent
{
    public readonly oldCenter:IFrame;
    public readonly center:IFrame;
    public readonly zoom:APositiveNumber;
    public readonly pitch:APositiveNumber;
    public readonly bearing:APositiveNumber;
    public readonly visibleRegion:IFrame;

    constructor(oldCenter:Location, center:Location, zoom:APositiveNumber, pitch:APositiveNumber, bearing:APositiveNumber, visibleRegion:AMapVisibleRegion)
    {
        this.oldCenter = new AFrame(
            [
                new FrameField('lat', Type.Number), new FrameField('lon', Type.Number)
            ],
            [
                [
                    oldCenter.lat, oldCenter.long
                ]
            ]
        );
        this.center = new AFrame(
            [
                new FrameField('lat', Type.Number), new FrameField('lon', Type.Number)
            ],
            [
                [
                    center.lat, center.long
                ]
            ]
        );

        this.zoom = zoom;

        this.pitch = pitch;
        this.bearing = bearing;

        this.visibleRegion = new AFrame(
            [
                new FrameField('from', Type.Frame), new FrameField('to', Type.Frame)
            ],
            [
                [
                    visibleRegion.from, visibleRegion.to
                ]
            ]
        );
    }
}

export class AMapOnZoomChangeEvent
{
    public readonly oldCenter:IFrame;
    public readonly center:IFrame;
    public readonly oldZoom:APositiveNumber;
    public readonly zoom:APositiveNumber;
    public readonly pitch:APositiveNumber;
    public readonly bearing:APositiveNumber;
    public readonly visibleRegion:IFrame;

    constructor(oldCenter:Location, center:Location, oldZoom:APositiveNumber, zoom:APositiveNumber, pitch:APositiveNumber, bearing:APositiveNumber, visibleRegion:AMapVisibleRegion)
    {
        this.oldCenter = new AFrame(
            [
                new FrameField('lat', Type.Number), new FrameField('lon', Type.Number)
            ],
            [
                [
                    oldCenter.lat, oldCenter.long
                ]
            ]
        );
        this.center = new AFrame(
            [
                new FrameField('lat', Type.Number), new FrameField('lon', Type.Number)
            ],
            [
                [
                    center.lat, center.long
                ]
            ]
        );

        this.oldZoom = oldZoom;
        this.zoom = zoom;

        this.pitch = pitch;
        this.bearing = bearing;

        this.visibleRegion = new AFrame(
            [
                new FrameField('from', Type.Frame), new FrameField('to', Type.Frame)
            ],
            [
                [
                    visibleRegion.from, visibleRegion.to
                ]
            ]
        );
    }
}

export class AChartPointClickedEvent
{
    public readonly seriesIndex:ANumber;
    public readonly itemIndex:ANumber;

    constructor(seriesIndex:number, itemIndex:number)
    {
        this.seriesIndex = ANumber.fromNumber(seriesIndex);
        this.itemIndex = ANumber.fromNumber(itemIndex);
    }
}

export class AChartPointMouseHoverEvent
{
    public readonly seriesIndex:ANumber;
    public readonly itemIndex:ANumber;
    public readonly chartX:ANumber;
    public readonly chartY:ANumber;
    public readonly pageX:ANumber;
    public readonly pageY:ANumber;
    public readonly hovered:ABoolean;

    constructor(seriesIndex:number, itemIndex:number, chartX:number, chartY:number, pageX:number, pageY:number, hovered:boolean)
    {
        this.seriesIndex = ANumber.fromNumber(seriesIndex);
        this.itemIndex = ANumber.fromNumber(itemIndex);
        this.chartX = ANumber.fromNumber(chartX);
        this.chartY = ANumber.fromNumber(chartY);
        this.pageX = ANumber.fromNumber(pageX);
        this.pageY = ANumber.fromNumber(pageY);
        this.hovered = ABoolean.fromBoolean(hovered);
    }
}

export class ALoadedEvent
{
    public readonly url:AString | AError;

    constructor(url:string, errorMsg:string)
    {
        this.url = errorMsg ? AError.fromString(errorMsg) : AString.fromString(url);
    }
}

export class AResizeEvent
{
    public readonly width:ANumber;
    public readonly height:ANumber;
    public readonly type:AString | null;

    constructor(width:number, height:number, type?:string)
    {
        this.width = ANumber.fromNumber(width);
        this.height = ANumber.fromNumber(height);
        this.type = typeof type === 'string' ? AString.fromString(type) : null;
    }
}

export class APostMessageEvent
{
    public readonly data:IFrame | AError;

    constructor(data:any)
    {
        this.data = JsonUtil.convertJsonObjectToFrame(data);
    }
}

export class APageAppearingEvent
{
    public readonly pageName:AString;
    public readonly isOverlay:ABoolean;

    constructor(pageName:string, isOverlay:boolean)
    {
        this.pageName = new AString(pageName);
        this.isOverlay = new ABoolean(isOverlay);
    }
}

export class AValidateEvent
{
    constructor()
    {
    }
}
